/** @returns {Record<string, string>} Theme.routes from the classic-script `const Theme` in snippets/scripts.liquid, or {}. */
export function themeRoutes() {
  return (typeof Theme !== 'undefined' && Theme.routes) || {};
}

/** @returns {string[]} unique data-section-id values of the page's cart-items components. */
export function cartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

/**
 * Preorder line-item properties from the data-preorder* attributes; names must match snippets/ca-preorder-line.liquid.
 * @param {Element | null | undefined} el
 * @returns {Record<string, string> | null}
 */
export function preorderProperties(el) {
  if (!el || el.getAttribute('data-preorder') !== 'true') return null;
  /** @type {Record<string, string>} */
  const props = { _preorder: 'true' };
  const label = el.getAttribute('data-preorder-label');
  const ships = el.getAttribute('data-preorder-ships');
  const tag = el.getAttribute('data-preorder-tag');
  if (label) props.Availability = label;
  if (ships) props['Ships by'] = ships;
  if (tag) props._preorder_tag = tag;
  return props;
}

/**
 * Cart-wide item count printed into [ref="cartItemCount"] of a re-rendered cart section.
 * @param {Record<string, string> | undefined | null} sections
 * @returns {number | null}
 */
export function itemCountFromSections(sections) {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('cartItemCount')) continue;
    try {
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
      const parsed = parseInt((node?.textContent ?? '').trim(), 10);
      if (!Number.isNaN(parsed)) return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

/**
 * POSTs one JSON line item to the cart add endpoint; rejects only on network failure.
 * @param {object} options
 * @param {string | number} options.id - variant id, sent as given.
 * @param {number} [options.quantity]
 * @param {Record<string, string> | null} [options.properties]
 * @param {string | number} [options.sellingPlan]
 * @param {string} [options.url] - defaults to Theme.routes.cart_add_url, then /cart/add.js.
 * @param {string[] | string | null} [options.sections] - sections to re-render; null omits, undefined means every cart-items section.
 * @returns {Promise<{ response: Response, data: any }>} data is null when the body is not JSON.
 */
export async function addToCart({ id, quantity = 1, properties, sellingPlan, url, sections = cartSectionIds() }) {
  /** @type {Record<string, any>} */
  const item = { id, quantity };
  if (sellingPlan) item.selling_plan = sellingPlan;
  if (properties) item.properties = properties;
  /** @type {Record<string, any>} */
  const body = { items: [item] };
  if (sections) {
    body.sections = Array.isArray(sections) ? sections.join(',') : sections;
    body.sections_url = window.location.pathname;
  }
  const response = await fetch(url || themeRoutes().cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  return { response, data };
}
